import { all, call, cancelled, delay, put, takeLatest } from 'redux-saga/effects';
import axiosConfig from './axiosConfig';
import { getWeldIqPaths } from './apiPaths';
import {
  FETCH_WELD_HEALTH,
  FETCH_WELD_ANALYTICS_BUNDLE,
  FETCH_WELD_ANALYTICS_PRODUCTION,
  FETCH_WELD_DETAIL,
  TRIGGER_WELD_PIPELINE,
  FETCH_INSPECTION_DATA,
  FETCH_INSPECTION_PASSES,
  LIVE_CHECK_ANOMALY,
  LIVE_CHECK_INDICATION,
  LIVE_PREDICT,
  LIVE_SCORE_PASS,
  FETCH_PIPELINE_EXECUTIONS,
} from './types';
import {
  fetchWeldHealthSuccess,
  fetchWeldHealthFailure,
  fetchWeldAnalyticsBundleSuccess,
  fetchWeldAnalyticsBundleFailure,
  fetchWeldAnalyticsBundleCancelled,
  fetchWeldAnalyticsProductionSuccess,
  fetchWeldAnalyticsProductionFailure,
  fetchWeldAnalyticsProductionCancelled,
  fetchWeldDetailSuccess,
  fetchWeldDetailFailure,
  triggerWeldPipelineSuccess,
  triggerWeldPipelineFailure,
  fetchInspectionDataSuccess,
  fetchInspectionDataFailure,
  fetchInspectionPassesSuccess,
  fetchInspectionPassesFailure,
  liveCheckAnomalySuccess,
  liveCheckAnomalyFailure,
  liveCheckIndicationSuccess,
  liveCheckIndicationFailure,
  livePredictSuccess,
  livePredictFailure,
  liveScorePassSuccess,
  liveScorePassFailure,
  fetchPipelineExecutionsSuccess,
  fetchPipelineExecutionsFailure,
} from './actions';
import { Notification } from '../../components/ui/custom-notification';
import { fetchWeldStatusForAnalytics } from '../../features/weld-analytics/utils/fetchWeldStatusForAnalytics';
import { extractWeldiqApiError } from '../../features/weld-analytics/utils/extractWeldiqApiError';
import {
  allPartialErrorsArePipelineUnavailable,
  isWeldIqPipelineUnavailableMessage,
  sanitizeWeldIqUserMessage,
} from '../../features/weld-analytics/utils/weldIqEmptyState';
import { weldIqProjectIdsToTry } from '../../features/weld-analytics/utils/weldIqProjectId';
import { partitionScoredWelds } from '../../features/weld-analytics/utils/scoredJobMatch';
import {
  checkAnomaly,
  checkIndication,
  predictReject,
  scorePassRealtime,
  fetchInspectionJobs,
  fetchInspectionWelds,
  fetchInspectionPasses,
  fetchNdtRecordsViaWeldiq,
  listPipelineExecutions,
  warmupScoringLambdas,
} from '../../features/weld-analytics/api/weldiqLiveApi';

function emptyUnavailableBundle(filters, jobs, partialErrors) {
  return {
    filters: { ...filters, jobNumbers: jobs },
    welds: [],
    weldMeta: { total: 0, truncated: false, pagesFetched: 0 },
    anomalies: [],
    anomalyMeta: { total: 0, truncated: false, pagesFetched: 0 },
    compliance: null,
    anomalyMetrics: null,
    compliancePassType: null,
    complianceTrend: null,
    totalPasses: 0,
    wpsFailed: 0,
    partialErrors,
    weldIqUnavailable: 'pipeline',
  };
}

const paths = () => getWeldIqPaths();

const LIVE_CHECK_MAX_ATTEMPTS = 3;
const LIVE_CHECK_RETRY_DELAYS_MS = [500, 1000];

/** Gateway timeouts / client aborts should not be retried — retries stampede the DB. */
function isNonRetryableLiveCheckError(error) {
  const status = error?.response?.status;
  if (status === 504 || status === 503 || status === 429) return true;
  if (error?.code === 'ECONNABORTED') return true;
  const msg = String(error?.message || '');
  if (/timeout/i.test(msg)) return true;
  if (msg === 'Network Error') return true;
  return false;
}

const MAX_WELD_PAGES = 6;
const MAX_ANOMALY_PAGES = 6;
const PAGE_SIZE = 200;

function buildQuery(params) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== '') {
      q.set(k, String(v));
    }
  });
  return q.toString();
}

function isAbortError(error) {
  return (
    error?.name === 'AbortError' ||
    error?.code === 'ERR_CANCELED' ||
    error?.message === 'Canceled' ||
    error?.message === 'Aborted'
  );
}

async function fetchAllWelds(filters, signal) {
  const p = paths();
  const allWelds = [];
  let total = 0;
  let totalPages = 1;

  for (let page = 1; page <= MAX_WELD_PAGES && page <= totalPages; page += 1) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const qs = buildQuery({
      page,
      page_size: PAGE_SIZE,
      project_id: filters.projectId,
      job_number: filters.jobNumber,
      welding_system: filters.weldingSystem,
      anomaly_only: filters.anomalyOnly ? 'true' : undefined,
      date_from: filters.dateFrom,
      date_to: filters.dateTo,
    });
    const res = await axiosConfig.get(`${p.welds}?${qs}`, { signal });
    const data = res.data;
    total = data.total ?? total;
    totalPages = data.total_pages ?? totalPages;
    if (Array.isArray(data.welds)) {
      allWelds.push(...data.welds);
    }
    if (page >= totalPages) break;
  }

  return {
    welds: allWelds,
    total,
    truncated: totalPages > MAX_WELD_PAGES,
    pagesFetched: Math.min(totalPages, MAX_WELD_PAGES),
  };
}

async function fetchAllAnomalies(filters, signal) {
  const p = paths();
  const allAnomalies = [];
  let total = 0;
  let totalPages = 1;

  for (let page = 1; page <= MAX_ANOMALY_PAGES && page <= totalPages; page += 1) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const qs = buildQuery({
      page,
      page_size: PAGE_SIZE,
      project_id: filters.projectId,
      job_number: filters.jobNumber,
      welding_system: filters.weldingSystem,
      pass_family: filters.passFamily,
      date_from: filters.dateFrom,
      date_to: filters.dateTo,
    });
    const res = await axiosConfig.get(`${p.anomalies}?${qs}`, { signal });
    const data = res.data;
    total = data.total ?? data.anomalous ?? total;
    totalPages = data.total_pages ?? totalPages;
    if (Array.isArray(data.anomalies)) {
      allAnomalies.push(...data.anomalies);
    }
    if (page >= totalPages) break;
  }

  return {
    anomalies: allAnomalies,
    total,
    truncated: totalPages > MAX_ANOMALY_PAGES,
    pagesFetched: Math.min(totalPages, MAX_ANOMALY_PAGES),
  };
}

function jobErrorMessage(err) {
  return String(err?.error || err?.message || 'Failed to load');
}

async function settledOrAbort(promise) {
  try {
    return { ok: true, value: await promise };
  } catch (err) {
    if (isAbortError(err)) throw err;
    return { ok: false, error: jobErrorMessage(err) };
  }
}

/** When selected jobs are missing from the filtered rows, read the project file and keep normalized matches. */
async function withUnfilteredWeldCoverage(filters, jobs, bundle, signal) {
  const existing = Array.isArray(bundle?.welds) ? bundle.welds : [];
  const current = partitionScoredWelds(existing, jobs);
  if (existing.length > 0 && current.missingJobs.length === 0) {
    return {
      ...bundle,
      scoredJobsInFile: current.jobsInFile,
      scoredJobsMissing: [],
    };
  }

  try {
    const unfiltered = await fetchAllWelds({ ...filters, jobNumber: undefined }, signal);
    const part = partitionScoredWelds(unfiltered.welds, jobs);
    const fileHasRows = unfiltered.welds.length > 0;
    const matched = part.matched.length > 0 ? part.matched : existing;
    return {
      ...bundle,
      welds: matched,
      weldMeta: {
        total: matched.length,
        truncated: unfiltered.truncated,
        pagesFetched: unfiltered.pagesFetched,
      },
      scoredJobsInFile: part.jobsInFile,
      scoredJobsMissing: part.missingJobs,
      weldIqUnavailable: fileHasRows ? undefined : bundle.weldIqUnavailable,
    };
  } catch (err) {
    if (isAbortError(err)) throw err;
    return {
      ...bundle,
      scoredJobsInFile: current.jobsInFile,
      scoredJobsMissing: current.missingJobs,
    };
  }
}

function mergeAnomalyMetrics(perJobMetrics) {
  const valid = perJobMetrics.filter((m) => m && typeof m === 'object' && Object.keys(m).length > 0);
  if (valid.length === 0) return null;
  if (valid.length === 1) return valid[0];

  const sum = (key) =>
    valid.reduce((total, m) => {
      const n = Number(m[key]);
      return total + (Number.isFinite(n) ? n : 0);
    }, 0);

  const tp = sum('pass_proxy_tp');
  const fp = sum('pass_proxy_fp');
  const fn = sum('pass_proxy_fn');
  const jtp = sum('joint_proxy_tp');
  const jfp = sum('joint_proxy_fp');
  const jfn = sum('joint_proxy_fn');

  return {
    total_welds: sum('total_welds'),
    num_rule_based: sum('num_rule_based'),
    num_per_system: sum('num_per_system'),
    num_global: sum('num_global'),
    num_rule_and_per_system: sum('num_rule_and_per_system'),
    num_only_per_system: sum('num_only_per_system'),
    num_only_rule_based: sum('num_only_rule_based'),
    pass_proxy_tp: tp,
    pass_proxy_fp: fp,
    pass_proxy_fn: fn,
    pass_proxy_precision_pct: Math.round((tp / Math.max(tp + fp, 1)) * 10000) / 100,
    pass_proxy_recall_pct: Math.round((tp / Math.max(tp + fn, 1)) * 10000) / 100,
    joint_proxy_tp: jtp,
    joint_proxy_fp: jfp,
    joint_proxy_fn: jfn,
    joint_proxy_precision_pct: Math.round((jtp / Math.max(jtp + jfp, 1)) * 10000) / 100,
    joint_proxy_recall_pct: Math.round((jtp / Math.max(jtp + jfn, 1)) * 10000) / 100,
  };
}

/**
 * Merge per-job bundle results into a single bundle. Multi-job is implemented
 * client-side (fan-out) because the WeldIQ API currently accepts only one
 * job_number per call; tracked as a backend follow-up.
 */
function mergeBundles(filters, perJobResults) {
  if (perJobResults.length === 1) {
    return { ...perJobResults[0], filters };
  }

  const welds = perJobResults.flatMap((r) => r.welds || []);
  const anomalies = perJobResults.flatMap((r) => r.anomalies || []);
  const weldMeta = perJobResults.reduce(
    (acc, r) => ({
      total: acc.total + (r.weldMeta?.total || 0),
      truncated: acc.truncated || Boolean(r.weldMeta?.truncated),
      pagesFetched: acc.pagesFetched + (r.weldMeta?.pagesFetched || 0),
    }),
    { total: 0, truncated: false, pagesFetched: 0 },
  );
  const anomalyMeta = perJobResults.reduce(
    (acc, r) => ({
      total: acc.total + (r.anomalyMeta?.total || 0),
      truncated: acc.truncated || Boolean(r.anomalyMeta?.truncated),
      pagesFetched: acc.pagesFetched + (r.anomalyMeta?.pagesFetched || 0),
    }),
    { total: 0, truncated: false, pagesFetched: 0 },
  );

  let weightedRateNumerator = 0;
  let weightedRateDenominator = 0;
  let totalChecked = 0;
  const perParamByName = new Map();
  perJobResults.forEach((r) => {
    const c = r.compliance;
    if (!c) return;
    const tp = Number(c.total_passes || 0);
    const rate = Number(c.overall_compliance_rate_pct ?? NaN);
    if (Number.isFinite(rate) && tp > 0) {
      weightedRateNumerator += rate * tp;
      weightedRateDenominator += tp;
    }
    totalChecked += Number(c.wps_params_checked || 0);
    (c.per_parameter || []).forEach((row) => {
      const existing = perParamByName.get(row.parameter);
      if (!existing) {
        perParamByName.set(row.parameter, { ...row });
      } else {
        existing.checked += row.checked || 0;
        existing.compliant += row.compliant || 0;
        existing.non_compliant += row.non_compliant || 0;
        existing.compliance_rate_pct =
          existing.checked > 0 ? (existing.compliant / existing.checked) * 100 : 0;
      }
    });
  });

  const compliance = perJobResults.some((r) => r.compliance)
    ? {
        job_number: filters.jobNumbers?.join(', ') || null,
        total_passes: perJobResults.reduce((s, r) => s + Number(r.compliance?.total_passes || 0), 0),
        wps_params_checked: totalChecked,
        overall_compliance_rate_pct:
          weightedRateDenominator > 0 ? weightedRateNumerator / weightedRateDenominator : null,
        per_parameter: Array.from(perParamByName.values()),
      }
    : null;

  const totalPasses = perJobResults.reduce((s, r) => s + (r.totalPasses || 0), 0);
  const wpsFailed = perJobResults.reduce((s, r) => s + (r.wpsFailed || 0), 0);

  return {
    filters,
    welds,
    weldMeta,
    anomalies,
    anomalyMeta,
    compliance,
    anomalyMetrics: mergeAnomalyMetrics(perJobResults.map((r) => r.anomalyMetrics)),
    compliancePassType: perJobResults.find((r) => r.compliancePassType)?.compliancePassType ?? null,
    complianceTrend: perJobResults.find((r) => r.complianceTrend)?.complianceTrend ?? null,
    totalPasses,
    wpsFailed,
    partialErrors: [],
  };
}

const api = {
  fetchHealth: async () => {
    const res = await axiosConfig.get(paths().health);
    return res.data;
  },

  fetchBundleForJob: async (filters, jobNumber, signal) => {
    const p = paths();
    const singleJobFilters = { ...filters, jobNumber };
    const projectParam = filters.projectId
      ? `project_id=${encodeURIComponent(String(filters.projectId))}`
      : '';
    const jobParam = jobNumber ? `job_number=${encodeURIComponent(jobNumber)}` : '';
    const metricsQuery = buildQuery({
      project_id: filters.projectId || undefined,
      job_number: jobNumber || undefined,
      welding_system: filters.weldingSystem || undefined,
    });
    const complianceUrl = (extra) => {
      const parts = [projectParam, jobParam, extra].filter(Boolean);
      return `${p.compliance}${parts.length ? `?${parts.join('&')}` : ''}`;
    };
    const axiosOpts = signal ? { signal } : {};

    const [weldSettled, anomalySettled, complianceRes, metricsRes, passTypeRes, trendRes] =
      await Promise.all([
        settledOrAbort(fetchAllWelds(singleJobFilters, signal)),
        settledOrAbort(fetchAllAnomalies(singleJobFilters, signal)),
        axiosConfig.get(complianceUrl(''), axiosOpts).catch(() => ({ data: null })),
        axiosConfig
          .get(metricsQuery ? `${p.anomalySummary}?${metricsQuery}` : p.anomalySummary, axiosOpts)
          .catch(() => ({ data: null })),
        axiosConfig.get(complianceUrl('breakdown=pass_type'), axiosOpts).catch(() => ({ data: null })),
        axiosConfig
          .get(complianceUrl('breakdown=trend&days=30'), axiosOpts)
          .catch(() => ({ data: null })),
      ]);

    if (!weldSettled.ok && !anomalySettled.ok) {
      throw new Error(weldSettled.error || anomalySettled.error || 'Failed to load job');
    }

    const weldResult = weldSettled.ok
      ? weldSettled.value
      : { welds: [], total: 0, truncated: false, pagesFetched: 0 };
    const anomalyResult = anomalySettled.ok
      ? anomalySettled.value
      : { anomalies: [], total: 0, truncated: false, pagesFetched: 0 };

    const compliance = complianceRes.data;
    const totalPasses =
      compliance?.total_passes ??
      weldResult.welds.reduce((s, w) => s + (w.total_passes || 0), 0);
    const wpsFailed =
      compliance?.per_parameter?.reduce((s, row) => s + (row.non_compliant || 0), 0) ??
      weldResult.welds.reduce((s, w) => s + (w.failed_passes || 0), 0);

    return {
      welds: weldResult.welds,
      weldMeta: {
        total: weldResult.total,
        truncated: weldResult.truncated,
        pagesFetched: weldResult.pagesFetched,
      },
      anomalies: anomalyResult.anomalies,
      anomalyMeta: {
        total: anomalyResult.total,
        truncated: anomalyResult.truncated,
        pagesFetched: anomalyResult.pagesFetched,
      },
      compliance,
      anomalyMetrics: metricsRes.data,
      compliancePassType: passTypeRes.data,
      complianceTrend: trendRes.data,
      totalPasses,
      wpsFailed,
    };
  },

  fetchBundleForProject: async (filters, signal) => {
    const rawJobs =
      Array.isArray(filters.jobNumbers) && filters.jobNumbers.length > 0
        ? filters.jobNumbers
        : filters.jobNumber
          ? [filters.jobNumber]
          : [];
    const jobs = [...new Set(rawJobs.map((j) => String(j).trim()).filter(Boolean))];

    if (jobs.length === 0) {
      throw new Error('At least one job number is required');
    }

    const FANOUT_CONCURRENCY = 3;
    const settled = new Array(jobs.length);
    let next = 0;
    const workers = Array.from(
      { length: Math.min(FANOUT_CONCURRENCY, jobs.length) },
      async () => {
        while (next < jobs.length) {
          if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
          const i = next;
          next += 1;
          const jn = jobs[i];
          try {
            const data = await api.fetchBundleForJob(filters, jn, signal);
            settled[i] = { ok: true, jobNumber: jn, data };
          } catch (err) {
            if (isAbortError(err)) throw err;
            const message = err?.error || err?.message || 'Failed to load job';
            settled[i] = { ok: false, jobNumber: jn, error: String(message) };
          }
        }
      },
    );
    await Promise.all(workers);

    const successes = settled.filter((r) => r.ok).map((r) => r.data);
    const partialErrors = settled
      .filter((r) => !r.ok)
      .map((r) => ({ jobNumber: r.jobNumber, error: r.error }));

    if (successes.length === 0) {
      if (allPartialErrorsArePipelineUnavailable(partialErrors)) {
        const unavailable = emptyUnavailableBundle(filters, jobs, partialErrors);
        return withUnfilteredWeldCoverage(filters, jobs, unavailable, signal);
      }
      const detail = partialErrors.map((e) => `${e.jobNumber}: ${e.error}`).join('; ');
      throw new Error(detail || 'Failed to load Weld Analytics for all selected jobs');
    }

    const bundle = mergeBundles({ ...filters, jobNumbers: jobs }, successes);
    return withUnfilteredWeldCoverage(
      filters,
      jobs,
      { ...bundle, partialErrors },
      signal,
    );
  },

  fetchBundle: async (filters, signal) => {
    const ids = weldIqProjectIdsToTry({
      projectId: filters?.projectId,
      projectName: filters?.projectName,
    });
    const attempts = ids.length > 0 ? ids : [filters?.projectId].filter((id) => id != null && id !== '');
    if (attempts.length === 0) {
      return api.fetchBundleForProject(filters, signal);
    }

    let emptyHit = null;
    let unavailableHit = null;
    let hardError = null;
    for (let index = 0; index < attempts.length; index += 1) {
      const projectId = attempts[index];
      try {
        const bundle = await api.fetchBundleForProject({ ...filters, projectId }, signal);
        if (bundle?.weldIqUnavailable) {
          unavailableHit = bundle;
          continue;
        }
        const hasRows =
          (bundle?.welds?.length || 0) > 0 || (bundle?.anomalies?.length || 0) > 0;
        if (hasRows || index === attempts.length - 1) return bundle;
        emptyHit = bundle;
      } catch (err) {
        if (isAbortError(err)) throw err;
        const message = err?.error || err?.message || String(err || '');
        if (isWeldIqPipelineUnavailableMessage(message)) {
          const jobs = Array.isArray(filters?.jobNumbers) ? filters.jobNumbers : [];
          unavailableHit = emptyUnavailableBundle(
            { ...filters, projectId },
            jobs,
            jobs.map((jobNumber) => ({ jobNumber: String(jobNumber), error: String(message) })),
          );
          continue;
        }
        hardError = err;
      }
    }
    if (emptyHit) return emptyHit;
    if (unavailableHit) return unavailableHit;
    throw hardError || new Error('Failed to load Weld Analytics');
  },

  fetchWeldDetail: async ({ weldNumber, jobNumber, projectId }) => {
    const p = paths();
    const style = process.env.REACT_APP_WELDIQ_API_PATH_STYLE || 'cloudfront';
    if (style === 'gateway') {
      const qs = buildQuery({
        project_id: projectId,
        job_number: jobNumber,
      });
      const res = await axiosConfig.get(
        `${p.welds}/${encodeURIComponent(weldNumber)}?${qs}`,
      );
      return res.data;
    }
    const qs = buildQuery({
      project_id: projectId,
      job_number: jobNumber,
      page: 1,
      page_size: 200,
    });
    const res = await axiosConfig.get(`${p.welds}?${qs}`);
    const found = (res.data.welds || []).find((w) => w.weld_number === weldNumber);
    if (!found) {
      throw new Error('Weld not found');
    }
    return found;
  },

  triggerPipeline: async (pipelineType = 'full') => {
    const p = paths();
    const res = await axiosConfig.post(p.pipeline, { pipeline_type: pipelineType });
    return res.data;
  },
};

function* fetchWeldHealthSaga() {
  try {
    const health = yield call(api.fetchHealth);
    yield put(fetchWeldHealthSuccess(health));
  } catch (error) {
    const msg = error?.error || error?.message || 'WeldIQ health check failed';
    yield put(fetchWeldHealthFailure(msg));
  }
}

function* fetchWeldAnalyticsBundleSaga(action) {
  const requestId = action.payload?.requestId;
  const controller = new AbortController();
  try {
    const bundle = yield call(api.fetchBundle, action.payload, controller.signal);
    yield put(fetchWeldAnalyticsBundleSuccess({ ...bundle, requestId }));
  } catch (error) {
    if (isAbortError(error) || (yield cancelled())) {
      yield put(fetchWeldAnalyticsBundleCancelled({ requestId }));
      return;
    }
    const raw =
      error?.error ||
      error?.message ||
      (error?.status === 503
        ? 'Pipeline data not available. Run the WeldIQ pipeline or contact an administrator.'
        : 'Failed to load Weld Analytics');
    if (isWeldIqPipelineUnavailableMessage(raw)) {
      const jobs = Array.isArray(action.payload?.jobNumbers)
        ? action.payload.jobNumbers
        : action.payload?.jobNumber
          ? [action.payload.jobNumber]
          : [];
      const soft = emptyUnavailableBundle(
        action.payload || {},
        jobs,
        jobs.map((jobNumber) => ({ jobNumber: String(jobNumber), error: String(raw) })),
      );
      yield put(fetchWeldAnalyticsBundleSuccess({ ...soft, requestId }));
      return;
    }
    const msg = sanitizeWeldIqUserMessage(raw) || 'Failed to load Weld Analytics';
    yield put(fetchWeldAnalyticsBundleFailure(msg, { requestId }));
    Notification.error(msg);
  } finally {
    if (yield cancelled()) {
      controller.abort();
    }
  }
}

function* fetchWeldAnalyticsProductionSaga(action) {
  const requestId = action.payload?.requestId;
  const controller = new AbortController();
  try {
    const result = yield call(fetchWeldStatusForAnalytics, {
      projectId: action.payload?.projectId,
      projectName: action.payload?.projectName,
      pipeIds: action.payload?.pipeIds,
      signal: controller.signal,
    });
    yield put(
      fetchWeldAnalyticsProductionSuccess({
        ...result,
        requestId,
      }),
    );
  } catch (error) {
    if (isAbortError(error) || (yield cancelled())) {
      yield put(fetchWeldAnalyticsProductionCancelled({ requestId }));
      return;
    }
    const msg =
      extractWeldiqApiError(error) || error?.error || error?.message || 'Failed to load production weld data';
    yield put(fetchWeldAnalyticsProductionFailure(msg, { requestId }));
    Notification.error(msg);
  } finally {
    if (yield cancelled()) {
      controller.abort();
    }
  }
}

function* fetchWeldDetailSaga(action) {
  try {
    const detail = yield call(api.fetchWeldDetail, action.payload);
    yield put(fetchWeldDetailSuccess(detail));
  } catch (error) {
    const msg = error?.error || error?.message || 'Failed to load weld detail';
    yield put(fetchWeldDetailFailure(msg));
  }
}

function* triggerWeldPipelineSaga(action) {
  try {
    const result = yield call(api.triggerPipeline, action.payload?.pipelineType);
    yield put(triggerWeldPipelineSuccess(result));
    Notification.success('Pipeline trigger accepted');
    yield put({ type: FETCH_PIPELINE_EXECUTIONS, payload: { limit: 10 } });
  } catch (error) {
    const msg = error?.error || error?.message || 'Pipeline trigger failed';
    yield put(triggerWeldPipelineFailure(msg));
    Notification.error(msg);
  }
}

function buildNdtMap(records) {
  const map = {};
  (records || []).forEach((r) => {
    const wn = String(r.weld_number ?? r.weldNumber ?? '');
    const jn = String(r.job_number ?? r.jobNumber ?? '');
    if (!wn) return;
    const key = `${jn}::${wn}`;
    if (!map[key]) map[key] = [];
    map[key].push(r);
  });
  return map;
}

function* fetchInspectionDataSaga(action) {
  try {
    const { projectId, jobNumbers = [] } = action.payload || {};
    const [jobs, welds, ndtRecords] = yield all([
      call(fetchInspectionJobs, projectId),
      call(fetchInspectionWelds, projectId, jobNumbers),
      call(fetchNdtRecordsViaWeldiq),
    ]);
    yield put(
      fetchInspectionDataSuccess({
        jobs,
        welds,
        ndtMap: buildNdtMap(ndtRecords),
      }),
    );
  } catch (error) {
    const msg = error?.response?.data?.error || error?.message || 'Inspection data failed';
    yield put(fetchInspectionDataFailure(msg));
  }
}

function* fetchInspectionPassesSaga(action) {
  try {
    const { projectId, jobNumbers, weldNumber, weldKey } = action.payload || {};
    const passes = yield call(fetchInspectionPasses, projectId, jobNumbers, weldNumber);
    yield put(fetchInspectionPassesSuccess({ weldKey, passes }));
  } catch (error) {
    const msg = error?.response?.data?.error || error?.message || 'Failed to load passes';
    yield put(fetchInspectionPassesFailure(msg));
  }
}

function* liveCheckWithRetry(apiFn, payload) {
  yield call(warmupScoringLambdas);
  let lastError;
  for (let attempt = 0; attempt < LIVE_CHECK_MAX_ATTEMPTS; attempt += 1) {
    try {
      return yield call(apiFn, payload);
    } catch (error) {
      lastError = error;
      if (isNonRetryableLiveCheckError(error)) {
        break;
      }
      if (attempt < LIVE_CHECK_MAX_ATTEMPTS - 1) {
        yield delay(LIVE_CHECK_RETRY_DELAYS_MS[attempt] ?? 1000);
      }
    }
  }
  throw lastError;
}

function* liveCheckAnomalySaga(action) {
  const { weldKey, ...payload } = action.payload || {};
  try {
    const result = yield call(liveCheckWithRetry, checkAnomaly, payload);
    yield put(
      liveCheckAnomalySuccess({
        weldKey,
        result,
        updatedAt: Date.now(),
      }),
    );
  } catch (error) {
    const msg =
      extractWeldiqApiError(error) ||
      error?.response?.data?.error ||
      error?.message ||
      'Live anomaly check failed';
    yield put(liveCheckAnomalyFailure(msg, { weldKey }));
  }
}

function* liveCheckIndicationSaga(action) {
  const { weldKey, ...payload } = action.payload || {};
  try {
    const result = yield call(liveCheckWithRetry, checkIndication, payload);
    yield put(
      liveCheckIndicationSuccess({
        weldKey,
        result,
        updatedAt: Date.now(),
      }),
    );
  } catch (error) {
    const msg =
      extractWeldiqApiError(error) ||
      error?.response?.data?.error ||
      error?.message ||
      'Live indication check failed';
    yield put(liveCheckIndicationFailure(msg, { weldKey }));
  }
}

function* livePredictSaga(action) {
  const { weldKey, ...payload } = action.payload || {};
  try {
    const result = yield call(predictReject, payload);
    yield put(
      livePredictSuccess({
        weldKey,
        result,
        updatedAt: Date.now(),
      }),
    );
  } catch (error) {
    const msg = error?.response?.data?.error || error?.message || 'Prediction failed';
    yield put(livePredictFailure(msg, { weldKey }));
  }
}

function* liveScorePassSaga(action) {
  const { weldKey, body } = action.payload || {};
  try {
    const result = yield call(scorePassRealtime, body);
    yield put(
      liveScorePassSuccess({
        weldKey,
        result,
        updatedAt: Date.now(),
      }),
    );
  } catch (error) {
    const msg = error?.response?.data?.error || error?.message || 'Realtime score failed';
    yield put(liveScorePassFailure(msg, { weldKey }));
  }
}

function* fetchPipelineExecutionsSaga(action) {
  try {
    const limit = action.payload?.limit ?? 10;
    const executions = yield call(listPipelineExecutions, limit);
    yield put(fetchPipelineExecutionsSuccess(executions));
  } catch (error) {
    const msg = error?.response?.data?.error || error?.message || 'Failed to list pipeline runs';
    yield put(fetchPipelineExecutionsFailure(msg));
  }
}

export default function* weldAnalyticsSaga() {
  yield all([
    takeLatest(FETCH_WELD_HEALTH, fetchWeldHealthSaga),
    takeLatest(FETCH_WELD_ANALYTICS_BUNDLE, fetchWeldAnalyticsBundleSaga),
    takeLatest(FETCH_WELD_ANALYTICS_PRODUCTION, fetchWeldAnalyticsProductionSaga),
    takeLatest(FETCH_WELD_DETAIL, fetchWeldDetailSaga),
    takeLatest(TRIGGER_WELD_PIPELINE, triggerWeldPipelineSaga),
    takeLatest(FETCH_INSPECTION_DATA, fetchInspectionDataSaga),
    takeLatest(FETCH_INSPECTION_PASSES, fetchInspectionPassesSaga),
    takeLatest(LIVE_CHECK_ANOMALY, liveCheckAnomalySaga),
    takeLatest(LIVE_CHECK_INDICATION, liveCheckIndicationSaga),
    takeLatest(LIVE_PREDICT, livePredictSaga),
    takeLatest(LIVE_SCORE_PASS, liveScorePassSaga),
    takeLatest(FETCH_PIPELINE_EXECUTIONS, fetchPipelineExecutionsSaga),
  ]);
}

export { api, mergeBundles, isAbortError };
